/* ============================================================================
   TTTM.DESIGN PDP COMPONENTS for Prestige 10.3
   ----------------------------------------------------------------------------
   Plain custom elements, no dependencies, no build step. Load with `defer`.

     <tttm-wishlist-button>  add/remove a product, persisted in localStorage
     <tttm-wishlist-count>   badge showing how many items are saved
     <tttm-wishlist-list>    renders the saved products on the wishlist page
     <tttm-live-view>        randomised "N people are viewing this"
     <tttm-flash-sold>       randomised "N sold in last H hours"
     <tttm-compare-color>    show/hide columns in the compare-color modal

   Every element degrades gracefully: if the script fails to load the markup is
   still valid HTML and the page stays usable.
   ========================================================================== */

(() => {
  'use strict';

  const STORAGE_KEY = 'tttm:wishlist';
  const CHANGE_EVENT = 'tttm:wishlist:change';

  /* ==========================================================================
     WISHLIST STORE
     Single source of truth. localStorage-backed, and it broadcasts on every
     change so buttons and badges anywhere on the page stay in sync.
     ====================================================================== */

  const WishlistStore = {
    _cache: null,

    read() {
      if (this._cache) return this._cache;

      try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? [];
        this._cache = Array.isArray(parsed) ? parsed : [];
      } catch {
        // Private browsing, exceeded quota, or corrupted data.
        this._cache = [];
      }

      return this._cache;
    },

    write(items) {
      this._cache = items;

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch {
        // Storage unavailable — keep the in-memory copy for this session so the
        // UI still responds, it just won't survive a reload.
      }

      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
    },

    has(handle) {
      return this.read().some((item) => item.handle === handle);
    },

    /** @returns {boolean} true if the item was added, false if removed */
    toggle(entry) {
      const items = this.read();
      const index = items.findIndex((item) => item.handle === entry.handle);

      if (index > -1) {
        this.write(items.filter((_, i) => i !== index));
        return false;
      }

      this.write([entry, ...items]);
      return true;
    },

    remove(handle) {
      this.write(this.read().filter((item) => item.handle !== handle));
    },

    get count() {
      return this.read().length;
    }
  };

  // Keep multiple open tabs in agreement.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    WishlistStore._cache = null;
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { items: WishlistStore.read() } })
    );
  });

  // Exposed for debugging and for any other theme code that wants to hook in.
  window.TttmWishlist = WishlistStore;

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  /* ==========================================================================
     <tttm-wishlist-button>
     ====================================================================== */

  define(
    'tttm-wishlist-button',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;

        this.onClick = this.onClick.bind(this);
        this.sync = this.sync.bind(this);

        this.button.addEventListener('click', this.onClick);
        document.addEventListener(CHANGE_EVENT, this.sync);

        this.sync();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        document.removeEventListener(CHANGE_EVENT, this.sync);
      }

      onClick(event) {
        event.preventDefault();

        const handle = this.getAttribute('handle');
        if (!handle) return;

        const added = WishlistStore.toggle({
          handle,
          title: this.getAttribute('title') ?? '',
          url: this.getAttribute('url') ?? '',
          image: this.getAttribute('image') ?? '',
          price: this.getAttribute('price') ?? '',
          addedAt: Date.now()
        });

        // Let the rest of the theme react — analytics, toasts, and so on.
        this.dispatchEvent(
          new CustomEvent('tttm:wishlist:toggled', {
            bubbles: true,
            detail: { handle, added }
          })
        );
      }

      sync() {
        if (!this.button) return;

        const active = WishlistStore.has(this.getAttribute('handle'));
        const label = active
          ? this.getAttribute('label-remove') ?? 'Remove from wishlist'
          : this.getAttribute('label-add') ?? 'Add to wishlist';

        this.button.setAttribute('aria-pressed', String(active));
        this.button.setAttribute('aria-label', label);

        const text = this.button.querySelector('[data-wishlist-text]');
        if (text) text.textContent = label;
      }
    }
  );

  /* ==========================================================================
     <tttm-wishlist-count>
     ====================================================================== */

  define(
    'tttm-wishlist-count',
    class extends HTMLElement {
      connectedCallback() {
        this.sync = this.sync.bind(this);
        document.addEventListener(CHANGE_EVENT, this.sync);
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener(CHANGE_EVENT, this.sync);
      }

      sync() {
        const { count } = WishlistStore;
        this.textContent = String(count);
        this.toggleAttribute('hidden', count === 0);
      }
    }
  );

  /* ==========================================================================
     <tttm-wishlist-list>
     ====================================================================== */

  define(
    'tttm-wishlist-list',
    class extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.empty = this.querySelector('[data-wishlist-empty]');

        this.render = this.render.bind(this);
        document.addEventListener(CHANGE_EVENT, this.render);
        this.addEventListener('click', this.onClick);

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(CHANGE_EVENT, this.render);
      }

      onClick(event) {
        const trigger = event.target.closest('[data-wishlist-remove]');
        if (!trigger) return;

        event.preventDefault();
        WishlistStore.remove(trigger.getAttribute('data-wishlist-remove'));
      }

      render() {
        if (!this.grid) return;

        const items = WishlistStore.read();
        const removeLabel = this.getAttribute('label-remove') ?? 'Remove';

        this.grid.replaceChildren();
        this.grid.toggleAttribute('hidden', items.length === 0);
        this.empty?.toggleAttribute('hidden', items.length > 0);

        for (const item of items) {
          this.grid.appendChild(this.buildCard(item, removeLabel));
        }
      }

      buildCard(item, removeLabel) {
        // Built with the DOM API rather than innerHTML so a product title
        // containing markup can't inject anything.
        const card = document.createElement('div');
        card.className = 'tttm-wishlist-card v-stack gap-4';

        const link = document.createElement('a');
        link.href = item.url || '#';
        link.className = 'v-stack gap-3';

        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = item.title || '';
          img.loading = 'lazy';
          img.className = 'tttm-wishlist-card__image';
          link.appendChild(img);
        }

        const title = document.createElement('span');
        title.className = 'h6';
        title.textContent = item.title || item.handle;
        link.appendChild(title);

        if (item.price) {
          const price = document.createElement('span');
          price.className = 'text-subdued';
          price.textContent = item.price;
          link.appendChild(price);
        }

        card.appendChild(link);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'link text-subdued';
        remove.dataset.wishlistRemove = item.handle;
        remove.textContent = removeLabel;
        card.appendChild(remove);

        return card;
      }
    }
  );

  /* ==========================================================================
     <tttm-live-view>
     Randomised visitor counter. To be clear about what this is: the number is
     generated here in the browser. It is not a real analytics feed.
     ====================================================================== */

  define(
    'tttm-live-view',
    class extends HTMLElement {
      connectedCallback() {
        this.output = this.querySelector('[data-count]');
        if (!this.output) return;

        let min = parseInt(this.getAttribute('min'), 10) || 10;
        let max = parseInt(this.getAttribute('max'), 10) || 40;
        if (min > max) [min, max] = [max, min];

        this.min = min;
        this.max = max;
        this.interval = (parseInt(this.getAttribute('interval'), 10) || 7) * 1000;

        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.tick();
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      // No point burning a timer on a tab nobody is looking at.
      onVisibilityChange() {
        document.hidden ? this.stop() : this.start();
      }

      start() {
        this.timer ??= setInterval(() => this.tick(), this.interval);
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }

      tick() {
        const value = Math.floor(Math.random() * (this.max - this.min + 1)) + this.min;
        this.output.textContent = String(value);
      }
    }
  );

  /* ==========================================================================
     <tttm-flash-sold>
     Seeded from product + date, so the figure holds steady for a visitor
     through the day instead of jumping on every reload.
     ====================================================================== */

  define(
    'tttm-flash-sold',
    class extends HTMLElement {
      // FNV-1a — small, fast, and deterministic.
      static hash(input) {
        let h = 2166136261;
        for (let i = 0; i < input.length; i++) {
          h ^= input.charCodeAt(i);
          h = Math.imul(h, 16777619);
        }
        return Math.abs(h);
      }

      static pick(seed, min, max) {
        if (min > max) [min, max] = [max, min];
        return (this.hash(seed) % (max - min + 1)) + min;
      }

      connectedCallback() {
        const { constructor: C } = this;
        const seed = `${this.getAttribute('product') ?? 'product'}:${new Date()
          .toISOString()
          .slice(0, 10)}`;

        const sold = this.querySelector('[data-sold]');
        const hours = this.querySelector('[data-hours]');

        if (sold) {
          sold.textContent = String(
            C.pick(
              `${seed}:sold`,
              parseInt(this.getAttribute('min-sold'), 10) || 5,
              parseInt(this.getAttribute('max-sold'), 10) || 30
            )
          );
        }

        if (hours) {
          hours.textContent = String(
            C.pick(
              `${seed}:hours`,
              parseInt(this.getAttribute('min-hours'), 10) || 6,
              parseInt(this.getAttribute('max-hours'), 10) || 24
            )
          );
        }

        // Rendered hidden so the placeholder never flashes before the numbers land.
        this.removeAttribute('hidden');
      }
    }
  );

  /* ==========================================================================
     <tttm-compare-color>
     ====================================================================== */

  define(
    'tttm-compare-color',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const toggle = event.target.closest('[data-compare-toggle]');
        if (!toggle) return;

        event.preventDefault();

        const key = toggle.getAttribute('data-compare-toggle');
        const column = this.querySelector(`[data-compare-column="${CSS.escape(key)}"]`);
        if (!column) return;

        const visible = this.querySelectorAll('[data-compare-column]:not([hidden])');
        const isVisible = !column.hasAttribute('hidden');

        // Never let the shopper hide the last remaining column.
        if (isVisible && visible.length <= 1) return;

        column.toggleAttribute('hidden', isVisible);
        toggle.setAttribute('aria-pressed', String(!isVisible));

        const [eye, eyeOff] = toggle.querySelectorAll('svg');
        if (eye && eyeOff) {
          eye.toggleAttribute('hidden', isVisible);
          eyeOff.toggleAttribute('hidden', !isVisible);
        }
      }
    }
  );

  /* ==========================================================================
     <tttm-sticky-atc>
     ----------------------------------------------------------------------------
     Fixed-bottom, always-visible sticky add-to-cart bar. Does not reimplement
     variant switching or cart submission — it drives Prestige's own
     <variant-picker> and <product-form> through their public APIs, so it stays
     correct even if those internals change in a future theme update.
     ====================================================================== */

  define(
    'tttm-sticky-atc',
    class extends HTMLElement {
      connectedCallback() {
        this.formId = this.getAttribute('form');
        this.labelAdd = this.getAttribute('label-add') ?? 'Add to cart';
        this.labelSoldOut = this.getAttribute('label-sold-out') ?? 'Sold out';
        this.labelPreOrder = this.getAttribute('label-pre-order') ?? this.labelAdd;

        const mapScript = this.querySelector('[data-tttm-variant-map]');
        try {
          this.variantMap = mapScript ? JSON.parse(mapScript.textContent) : {};
        } catch {
          this.variantMap = {};
        }

        this.select = this.querySelector('[data-tttm-sticky-variant]');
        this.image = this.querySelector('[data-tttm-sticky-image]');
        this.submitBtn = this.querySelector('[data-tttm-sticky-submit]');
        this.submitText = this.querySelector('[data-tttm-sticky-submit-text]');

        this.form = this.formId ? document.forms[this.formId] : null;

        this.onSelectChange = this.onSelectChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.select?.addEventListener('change', this.onSelectChange);
        this.form?.addEventListener('variant:change', this.onVariantChange);

        this.observeFooter();
      }

      disconnectedCallback() {
        this.select?.removeEventListener('change', this.onSelectChange);
        this.form?.removeEventListener('variant:change', this.onVariantChange);
        this.footerObserver?.disconnect();
      }

      // Sticky select changed → drive the real variant-picker, the same way a
      // click on the main swatches/dropdown would.
      onSelectChange() {
        const option = this.select.selectedOptions[0];
        if (!option) return;

        // Instant local feedback from the pre-rendered variant map, while the
        // fetch triggered below is still in flight.
        this.applyVariantData(option.value);

        const picker = document.querySelector(
          `variant-picker[form-id="${CSS.escape(this.formId)}"]`
        );
        if (!picker || typeof picker.selectCombination !== 'function') return;

        let optionValues;
        try {
          optionValues = JSON.parse(option.getAttribute('data-option-values'));
        } catch {
          return;
        }

        picker.selectCombination({ optionValues, productChange: false });
      }

      // Real variant-picker changed (anywhere on the page) → reflect it here.
      onVariantChange(event) {
        const variant = event.detail?.variant;
        if (!variant) return;

        if (this.select && this.select.value !== String(variant.id)) {
          this.select.value = variant.id;
        }

        this.applyVariantData(variant.id);
      }

      applyVariantData(variantId) {
        const data = this.variantMap[variantId];
        if (!data) return;

        if (this.image && data.image) this.image.src = data.image;

        if (this.submitBtn) this.submitBtn.disabled = !data.available;

        if (this.submitText) {
          this.submitText.textContent = !data.available
            ? this.labelSoldOut
            : data.preOrder
              ? this.labelPreOrder
              : this.labelAdd;
        }
      }

      // Slides the bar away once the footer would otherwise sit underneath it.
      observeFooter() {
        const footer = document.querySelector('.shopify-section--footer');
        if (!footer) return;

        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.classList.toggle('tttm-is-hidden-footer', entry.isIntersecting);
        });
        this.footerObserver.observe(footer);
      }
    }
  );
})();
